// bluaIQ — Diagnostic Takeoff v3 — client-side feature flag gate.
//
// This file is the ONLY place that decides whether the diagnostic v3
// surface is reachable from the customer-facing app. It is intentionally
// conservative: defaults are off, and the only ways to enable the surface
// are explicit developer-only signals.
//
// SAFETY: this module never exposes diagnostic v3 to a normal customer.
// It is opt-in for developers / internal review only.
//
// Activation precedence (any one is enough):
//   1. URL query string contains `?dev=1` (sticky — also writes to localStorage).
//   2. localStorage `bluaiq_dev_mode` === '1'.
//   3. Vite-time env `import.meta.env.VITE_TAKEOFF_DIAGNOSTIC_V3_ENABLED === 'true'`.
//
// Customers do NOT see the route, the nav item, or any diagnostic UI.

const LOCAL_STORAGE_KEY = 'bluaiq_dev_mode'
const QUERY_KEY = 'dev'

function readUrlOverride(): boolean {
  if (typeof window === 'undefined') return false
  try {
    const params = new URLSearchParams(window.location.search)
    if (params.get(QUERY_KEY) === '1') {
      try { window.localStorage.setItem(LOCAL_STORAGE_KEY, '1') } catch (_) { /* ignore */ }
      return true
    }
  } catch (_) { /* ignore */ }
  return false
}

function readLocalStorageOverride(): boolean {
  if (typeof window === 'undefined') return false
  try {
    return window.localStorage.getItem(LOCAL_STORAGE_KEY) === '1'
  } catch (_) {
    return false
  }
}

function readBuildTimeOverride(): boolean {
  // Vite injects import.meta.env at build time. We guard for non-Vite runtimes.
  try {
    const meta = import.meta as unknown as { env?: Record<string, string | undefined> }
    return meta?.env?.VITE_TAKEOFF_DIAGNOSTIC_V3_ENABLED === 'true'
  } catch (_) {
    return false
  }
}

/**
 * True when the diagnostic v3 surface should be reachable. Defaults
 * to false for normal customers.
 */
export function isDiagnosticV3VisibleToClient(): boolean {
  return readUrlOverride() || readLocalStorageOverride() || readBuildTimeOverride()
}

/** Helpers for the dev console / internal tooling — not used by customers. */
export function enableDiagnosticV3DevMode(): void {
  if (typeof window === 'undefined') return
  try { window.localStorage.setItem(LOCAL_STORAGE_KEY, '1') } catch (_) { /* ignore */ }
}

export function disableDiagnosticV3DevMode(): void {
  if (typeof window === 'undefined') return
  try { window.localStorage.removeItem(LOCAL_STORAGE_KEY) } catch (_) { /* ignore */ }
}
